/**
 * Client-side PREVIEW of the v3 reference-PnL trigger, per leg.
 *
 * This is the fallback, not the source of truth. When the pair engine is running
 * it publishes its own breakdown (GET /pair/reference-pnl/:portfolio) and the UI
 * renders that — one computation, so the screen and the bot cannot disagree.
 * This module only covers the case the engine cannot: the bot is STOPPED and the
 * operator is sanity-checking reference prices before pressing Start.
 *
 * It mirrors `PairReferencePnlMixin.build_reference_breakdown()` in
 * fortuna/algorithm/pair/worker/mixins/reference_pnl.py. Two rules that were
 * previously missing here and made this file lie about what the bot would do:
 *   - PER PAIR = ONE contract. `calculate_open_qty()` always places 1; the old
 *     code scaled by a `contract_per_trade` knob the v3 open path never read
 *     (since removed end to end).
 *   - the stale-reference guard. The engine treats the trigger as UNDEFINED when
 *     the reference belongs to another contract or sits more than 5% from the
 *     live price — the state right after a futures roll that failed to carry the
 *     reference. The old code drew a confident number there.
 *
 * Unit rule ([[feedback-vnd-units]]): futures are points × 100000; stock prices
 * are thousand-VND, so × 1000 to reach raw VND. Everything returned here is raw
 * VND — the caller divides by 1000 when it wants thousand-VND.
 */

/** Futures point value (VN30F). */
const FUTURE_MULTIPLIER = 100000;
/** Stock quote unit: prices are thousand-VND. */
const STOCK_UNIT = 1000;
/** HSX board lot. */
const LOT = 100;
/** Engine's MAX_REFERENCE_DRIFT (lifecycle.py). Keep the two in lockstep. */
const MAX_REFERENCE_DRIFT = 0.05;

/** A pair is ONE contract. Not configurable — see the module docstring. */
export const CONTRACTS_PER_PAIR = 1;

export interface PairPnlStockInput {
    symbol: string;
    /** Configured weight. Only the ratio between stocks matters. */
    proportion?: number | null;
    /** User-entered reference price, thousand VND. */
    referencePrice?: number | null;
    /** Live market price, thousand VND. */
    marketPrice?: number | null;
}

export interface PairPnlStockLeg {
    /** Shares the one-pair basket would buy (board-lot rounded). */
    quantity: number;
    /** Raw VND. null when the reference or the live price is missing. */
    pnl: number | null;
    /** Set when the leg's slice rounds below one board lot ⇒ contributes 0. */
    note?: string;
}

export interface PairReferencePnlBreakdown {
    /** Raw VND; null when the futures reference or market price is missing. */
    futurePnl: number | null;
    /** Per stock symbol. */
    stocks: Record<string, PairPnlStockLeg>;
    /**
     * futurePnl + Σ stock legs, raw VND. null when ANY leg is undefined — the
     * engine treats a missing price as "trigger undefined, don't open", so a
     * partial total would be a misleading number to act on.
     */
    total: number | null;
    /** Why `total` is null, in the engine's own vocabulary. */
    undefinedReason: string | null;
}

export interface PairReferencePnlInput {
    /** `future_instrument_price` from the config, index points. */
    futureReferencePrice?: number | null;
    /** Live futures price, index points. */
    futureMarketPrice?: number | null;
    /**
     * `future_instrument_price_symbol` — the contract the reference was entered
     * against. When it names anything other than `futureLegSymbol` the engine
     * refuses to open, so the preview must refuse to quote.
     */
    referenceSymbol?: string | null;
    /** The contract the futures leg actually trades. */
    futureLegSymbol?: string | null;
    /** SELL = short future + long stocks (the sign anchor). */
    activeSide: 'BUY' | 'SELL';
    stocks: PairPnlStockInput[];
    /**
     * Apply the engine's stale-reference refusal (default true).
     *
     * That guard belongs to the TRIGGER: an operator-entered reference that names
     * another contract, or sits >5% off the market, is a roll that lost its
     * reference and the bot must not act on it. Callers that reuse this
     * arithmetic to measure a KNOWN-good mark — today's open, say — have neither
     * failure mode, and for them a >5% move is the answer, not a reason to refuse
     * one.
     */
    guardReference?: boolean;
    /**
     * Size the basket on the REFERENCE prices rather than the live ones
     * (default false).
     *
     * The trigger sizes on live prices because it is pricing an order it would
     * place now — `calculate_stock_and_qty` does the same. A caller measuring a
     * pair that was opened at a past mark wants the basket that was BOUGHT at
     * that mark: fixed, not re-cut every tick as prices move.
     */
    sizeAtReference?: boolean;
}

/** Round down to a whole board lot, like the engine's `calculate_even_qty`. */
const evenQty = (qty: number): number => (Number.isFinite(qty) ? Math.floor(qty / LOT) * LOT : 0);

/** Bare ticker of an exchange-qualified instrument id ("HNXDS:VN30F2609"). */
const tickerOf = (id: string): string => id.split(':').pop() ?? id;

/**
 * Size the one-pair stock basket the way the engine does: split the futures
 * notional by proportion, biggest price first, and let the cheapest instrument
 * absorb the rounding residual.
 *
 * Exported because the notional pane sizes its basket from the LIVE futures
 * notional rather than from a reference price, and must do it with this
 * function — a second copy of board-lot splitting is a second thing to drift.
 *
 * Weights are normalised by their sum (the engine normalises to 1 in
 * `get_stock_proportion`; the form lets you be mid-edit, and an un-normalised
 * split would silently understate qty). Stocks without a live price are excluded
 * from the basket — their PnL is undefined anyway.
 */
export function sizeBasket(amount: number, stocks: PairPnlStockInput[]): Record<string, number> {
    const priced = stocks.filter((s) => !!s.marketPrice && s.marketPrice > 0);
    const totalWeight = priced.reduce((sum, s) => sum + (s.proportion || 0), 0);
    if (!priced.length || totalWeight <= 0 || amount <= 0) return {};

    const sorted = [...priced].sort((a, b) => (b.marketPrice as number) - (a.marketPrice as number));
    const qtyBySymbol: Record<string, number> = {};
    let remaining = amount;

    sorted.forEach((stock, index) => {
        const price = (stock.marketPrice as number) * STOCK_UNIT;
        const isLast = index === sorted.length - 1;
        const qty = isLast
            ? evenQty(remaining / price)
            : evenQty((amount * ((stock.proportion || 0) / totalWeight)) / price);
        qtyBySymbol[stock.symbol] = qty;
        remaining -= qty * price;
    });

    return qtyBySymbol;
}

/**
 * Same two checks as the engine's `_is_reference_usable`, in the same order.
 * Returns the reason it is NOT usable, or null when it is.
 */
function referenceBlockReason(
    referenceSymbol: string | null | undefined,
    futureLegSymbol: string | null | undefined,
    reference: number,
    market: number,
): string | null {
    if (referenceSymbol && futureLegSymbol && tickerOf(referenceSymbol) !== tickerOf(futureLegSymbol)) {
        return 'reference_unusable';
    }
    if (market > 0 && Math.abs(reference - market) / market > MAX_REFERENCE_DRIFT) {
        return 'reference_unusable';
    }
    return null;
}

export function computePairReferencePnl({
    futureReferencePrice,
    futureMarketPrice,
    referenceSymbol,
    futureLegSymbol,
    activeSide,
    stocks,
    guardReference = true,
    sizeAtReference = false,
}: PairReferencePnlInput): PairReferencePnlBreakdown {
    // SELL shorts the future and longs the stocks; BUY flips both legs.
    const sign = activeSide === 'SELL' ? 1 : -1;

    let undefinedReason: string | null = null;
    if (futureReferencePrice == null) {
        undefinedReason = 'no_future_reference';
    } else if (futureMarketPrice == null) {
        undefinedReason = 'no_future_price';
    } else if (guardReference) {
        undefinedReason = referenceBlockReason(
            referenceSymbol, futureLegSymbol, futureReferencePrice, futureMarketPrice,
        );
    }

    const futureDefined = undefinedReason === null;
    const futurePnl = futureDefined
        ? ((futureReferencePrice as number) - (futureMarketPrice as number)) *
          FUTURE_MULTIPLIER * CONTRACTS_PER_PAIR * sign
        : null;

    // Notional the basket is sized against: the reference future price, so the
    // basket reflects the pair the user intends to open, not today's drift.
    const filledAmount = futureDefined
        ? (futureReferencePrice as number) * FUTURE_MULTIPLIER * CONTRACTS_PER_PAIR
        : 0;
    const qtyBySymbol = sizeBasket(
        filledAmount,
        sizeAtReference
            ? stocks.map((s) => ({ ...s, marketPrice: s.referencePrice }))
            : stocks,
    );

    const legs: Record<string, PairPnlStockLeg> = {};
    let stockPnl = 0;

    for (const stock of stocks) {
        const qty = qtyBySymbol[stock.symbol] ?? 0;
        const ref = stock.referencePrice;
        const now = stock.marketPrice;
        if (!now) {
            legs[stock.symbol] = { quantity: qty, pnl: null };
            undefinedReason = undefinedReason ?? `no_stock_price:${stock.symbol}`;
            continue;
        }
        if (!futureDefined) {
            // Sizing never ran, so there is no qty to mark. Prices are still
            // reported by the caller from its own state.
            legs[stock.symbol] = { quantity: 0, pnl: null };
            continue;
        }
        // A missing reference outranks the lot check. Sizing can itself depend on
        // the reference (`sizeAtReference`), so a leg with no reference lands at
        // qty 0 — and reporting that as "below one board lot, contributes 0"
        // would turn an undefined leg into a defined total of zero.
        if (ref == null) {
            legs[stock.symbol] = { quantity: qty, pnl: null };
            undefinedReason = undefinedReason ?? `no_stock_reference:${stock.symbol}`;
            continue;
        }
        if (qty <= 0) {
            // The engine skips these, so they contribute exactly 0 to the
            // trigger — report that rather than a null, and say why.
            legs[stock.symbol] = {
                quantity: 0, pnl: 0, note: 'below one board lot at this notional',
            };
            continue;
        }
        // Long stock profits when price rises above the reference; SELL flips.
        const pnl = (now - ref) * qty * STOCK_UNIT * sign;
        legs[stock.symbol] = { quantity: qty, pnl };
        stockPnl += pnl;
    }

    return {
        futurePnl,
        stocks: legs,
        total: undefinedReason ? null : (futurePnl as number) + stockPnl,
        undefinedReason,
    };
}
